// UserApprovalPanel.jsx
// The admin surface for approving pending accounts (B32).
//
// WHY THIS EXISTS
// Migration 0003 parks every new signup in the holding tenant, inactive, and the
// access-token hook gives them no tenant claim — so they authenticate and see nothing.
// That gate has worked since August. What did not exist was any way to CLEAR it from
// inside the product: approving someone meant a curl with a bearer token, or two SQL
// statements run by hand in the Supabase dashboard.
//
// TENANT ASSIGNMENT IS THE ACCESS MODEL. There is no per-user layer behind it and no
// partial grant — putting someone in a tenant gives them everything it owns. So this UI
// defaults to creating a NEW tenant for each approved user and makes joining an existing
// one a deliberate, typed choice. The dangerous option should take more effort, not less.
//
// The endpoints do the real enforcement: GET/POST /api/admin/users/* are gated by
// requireRole, so a member who reached this component by any means still gets a 403.
// Hiding it from the nav is a courtesy, not a control.

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './UserApprovalPanel.css';

export default function UserApprovalPanel() {
  const [users, setUsers]     = useState(null);   // null = not loaded yet
  const [error, setError]     = useState('');
  const [busyId, setBusyId]   = useState(null);
  const [notice, setNotice]   = useState('');
  // Which pending user has the "join an existing tenant" field open, and what is in it.
  const [joinFor, setJoinFor] = useState(null);
  const [joinId, setJoinId]   = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res  = await apiFetch('/api/admin/users/pending');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
      setUsers([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function approve(user, tenantId) {
    setBusyId(user.id);
    setError('');
    setNotice('');
    try {
      const res = await apiFetch('/api/admin/users/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tenantId ? { userId: user.id, tenantId } : { userId: user.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Approval failed (${res.status})`);
      setNotice(data.notice || 'Approved.');
      setJoinFor(null);
      setJoinId('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (users === null) {
    return <div className="uap-empty">Loading…</div>;
  }

  return (
    <div className="uap">
      <h3 className="uap-title">Pending approvals</h3>
      <p className="uap-lede">
        Anyone who signs in gets an account, but no access until it is approved here.
        Until then they see an “awaiting approval” screen and no data.
      </p>

      {error  && <div className="uap-error">{error}</div>}
      {notice && <div className="uap-notice">{notice}</div>}

      {users.length === 0 ? (
        <div className="uap-empty">
          No one is waiting.
          <button type="button" className="uap-link" onClick={load}>Refresh</button>
        </div>
      ) : (
        <ul className="uap-list">
          {users.map(u => (
            <li key={u.id} className="uap-row">
              <div className="uap-who">
                <span className="uap-email">{u.email || '(no email on record)'}</span>
                {u.fullName && <span className="uap-name">{u.fullName}</span>}
                <span className="uap-when">
                  Registered {new Date(u.createdAt).toLocaleDateString(undefined,
                    { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>

              {joinFor === u.id ? (
                <div className="uap-join">
                  {/* Deliberately a raw UUID field rather than a tenant picker. A dropdown
                      of every tenant would make "give this stranger access to Gary's
                      entire CRM" a two-click action. Requiring the id to be looked up and
                      pasted is friction in the right place. */}
                  <label className="uap-join-label">
                    Existing tenant UUID — they will see <strong>everything</strong> it owns
                  </label>
                  <input
                    className="uap-input"
                    value={joinId}
                    onChange={e => setJoinId(e.target.value.trim())}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    spellCheck={false}
                  />
                  <div className="uap-join-actions">
                    <button
                      type="button"
                      className="uap-btn uap-btn--danger"
                      disabled={busyId === u.id || !joinId}
                      onClick={() => approve(u, joinId)}
                    >
                      {busyId === u.id ? 'Approving…' : 'Add to that tenant'}
                    </button>
                    <button
                      type="button"
                      className="uap-btn uap-btn--ghost"
                      onClick={() => { setJoinFor(null); setJoinId(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="uap-actions">
                  <button
                    type="button"
                    className="uap-btn uap-btn--primary"
                    disabled={busyId === u.id}
                    onClick={() => approve(u, null)}
                  >
                    {busyId === u.id ? 'Approving…' : 'Approve — own workspace'}
                  </button>
                  <button
                    type="button"
                    className="uap-btn uap-btn--ghost"
                    disabled={busyId === u.id}
                    onClick={() => setJoinFor(u.id)}
                  >
                    Join existing tenant…
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="uap-foot">
        An approved user must sign out and back in before the change takes effect — their
        workspace is written into the session when they sign in, so an existing session
        still shows nothing.
      </p>
    </div>
  );
}
